import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
} from "@mui/material";

import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
import httpclient from "../../../../Utils";

const formFields = {
  depo: "",
  depoCode: "",
  state: "",
  city: "",
  postCode: "",
  phone: "",
  fullAddress: "",
};

const validateForm = (formData) => {
  const errors = {};

  if (!formData.depo?.trim()) {
    errors.depo = "Depot name is required";
  }

  if (!formData.depoCode?.trim()) {
    errors.depoCode = "Depot code is required";
  } else if (formData.depoCode.length > 10) {
    errors.depoCode = "Depot code must be 10 characters or less";
  }

  if (!formData.state?.trim()) {
    errors.state = "State is required";
  }

  if (!formData.city?.trim()) {
    errors.city = "City is required";
  }

  if (!formData.postCode?.trim()) {
    errors.postCode = "Post code is required";
  }

  if (!formData.fullAddress?.trim()) {
    errors.fullAddress = "Full address is required";
  }

  if (formData.phone) {
    if (!/^\d*$/.test(formData.phone)) {
      errors.phone = "Phone number must contain only numbers";
    } else if (formData.phone.length > 10) {
      errors.phone = "Phone number cannot exceed 10 digits";
    }
  }

  return errors;
};

function AddDepot() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const mode = id ? "edit" : "add";
  const [loading, setLoading] = useState(mode === "edit");

  const [formData, setFormData] = useState(formFields);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  // Fetch depot data if in edit mode
  useEffect(() => {
    const fetchDepotData = async () => {
      if (mode === "edit" && id) {
        setLoading(true);

        const API_URL = `delivery/depo/${id}`;

        try {
          const res = await httpclient.get(API_URL);
          const { data } = res;

          if (data.status === 200) {
            const depotData = data.data;

            const modifiedData = {
              depo: depotData.depo || "",
              depoCode: depotData.depo_code || "",
              state: depotData.state || "",
              city: depotData.city || "",
              postCode: depotData.post_code || "",
              phone: depotData.phone || "",
              fullAddress: depotData.full_address || "",
            };
            setFormData(modifiedData);
          } else {
            showNotification(
              "error",
              data.message || "Failed to fetch depot data"
            );
          }
        } catch (error) {
          if (error.response?.status === 401) {
            refresh();
          } else {
            handleApiError(error);
          }
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDepotData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  const handleChange = (field) => (event) => {
    let value = event.target.value;
    
    // For phone field, only allow numbers
    if (field === "phone") {
      value = value.replace(/[^0-9]/g, '');
      // Limit to 10 digits
      if (value.length > 10) {
        value = value.slice(0, 10);
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field changes
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstError = Object.values(errors)[0];
      showNotification("error", firstError);
      return;
    }

    try {
      setIsSubmitting(true);

      const endpoint =
        mode === "edit" ? `delivery/depo/update/${id}` : "delivery/depo/add";

      const payload = {
        depo: formData.depo,
        depo_code: formData.depoCode,
        state: formData.state,
        city: formData.city,
        post_code: formData.postCode,
        phone: formData.phone,
        full_address: formData.fullAddress,
      };

      const { data } = await httpclient.post(endpoint, payload);

      if (data.status === 200) {
        showNotification("success", data.message);
        setTimeout(() => {
          navigate("/delivery/depo");
        }, 1000);
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Depot Name"
                variant="outlined"
                value={formData.depo}
                onChange={handleChange("depo")}
                error={!!formErrors.depo}
                helperText={formErrors.depo}
                placeholder="Enter depot name"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Depot Code"
                variant="outlined"
                value={formData.depoCode}
                onChange={handleChange("depoCode")}
                error={!!formErrors.depoCode}
                helperText={formErrors.depoCode}
                placeholder="Enter depot code"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Address"
                variant="outlined"
                value={formData.fullAddress}
                onChange={handleChange("fullAddress")}
                error={!!formErrors.fullAddress}
                helperText={formErrors.fullAddress}
                placeholder="Enter complete address"
                multiline
                rows={2}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="State"
                variant="outlined"
                value={formData.state}
                onChange={handleChange("state")}
                error={!!formErrors.state}
                helperText={formErrors.state}
                placeholder="Enter state"
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={formData.city}
                onChange={handleChange("city")}
                error={!!formErrors.city}
                helperText={formErrors.city}
                placeholder="Enter city"
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Post Code"
                variant="outlined"
                value={formData.postCode}
                onChange={handleChange("postCode")}
                error={!!formErrors.postCode}
                helperText={formErrors.postCode}
                placeholder="Enter post code"
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                value={formData.phone}
                onChange={handleChange("phone")}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                placeholder="Enter phone number (max 10 digits)"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 10
                }}
                // Not adding "required" prop to phone field
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{
                borderRadius: "4px",
                backgroundColor: "#7152F3",
                "&:hover": {
                  backgroundColor: "#5c34fa",
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                },
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                  {mode === "edit" ? "Updating..." : "Adding..."}
                </>
              ) : mode === "edit" ? (
                "Update Depot"
              ) : (
                "Add Depot"
              )}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/delivery/depo")}
              disabled={isSubmitting}
              sx={{
                borderRadius: "4px",
              }}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
}

export default AddDepot;